class BufferBitsWriter {
  private data: Uint8Array
  private offset: number

  constructor(data: Uint8Array) {
    this.data = data
    this.offset = 0
  }

  writeBits(value: number, length: number) {
    while (length > 0) {
      const avalailableBits = 8 - (this.offset % 8)
      const bitShift = Math.max(length - avalailableBits, 0)

      this.data[Math.floor(this.offset / 8)] |=
        ((value >> bitShift) & ((1 << avalailableBits) - 1)) <<
        Math.max(avalailableBits - length, 0)
      this.offset += length - bitShift

      length -= avalailableBits
      value &= (1 << bitShift) - 1
    }
  }
}

class BufferBitsReader {
  private data: Uint8Array
  private offset: number

  constructor(data: Uint8Array) {
    this.data = data
    this.offset = 0
  }

  readBits(length: number): number {
    let value = 0

    for (let i = 0; i < length; i++) {
      value =
        (value << 1) |
        ((this.data[Math.floor(this.offset / 8)] >> (7 - (this.offset % 8))) &
          0x1)
      this.offset++
    }

    return value
  }
}

export type SerializableHighlight = {
  page: number
  rects: {
    top: number
    left: number
    width: number
    height: number
  }[]
}

/*
  12 bits: number of highlights
  for each highlight:
    12 bits: page number
    12 bits: number of rects
    for each rect:
      12 bits: top
      12 bits: left
      12 bits: width
      12 bits: height
*/

const getBufferSize = (highlights: SerializableHighlight[]): number =>
  Math.ceil(
    highlights.reduce((acc, e) => acc + 12 * 2 + e.rects.length * 4 * 12, 12) /
      8
  )

export const encodeHighlight = (
  highlights: SerializableHighlight[]
): string => {
  const data = new Uint8Array(getBufferSize(highlights))
  const writer = new BufferBitsWriter(data)

  writer.writeBits(highlights.length, 12)

  for (let i = 0; i < highlights.length; i++) {
    const highlight = highlights[i]

    writer.writeBits(highlight.page, 12)
    writer.writeBits(highlight.rects.length, 12)

    for (let j = 0; j < highlight.rects.length; j++) {
      const rect = highlight.rects[j]
      writer.writeBits(rect.top * 4096, 12)
      writer.writeBits(rect.left * 4096, 12)
      writer.writeBits(rect.width * 4096, 12)
      writer.writeBits(rect.height * 4096, 12)
    }
  }

  return encodeURIComponent(Buffer.from(data).toString('base64'))
}

export const decodeHighlight = (b64: string): SerializableHighlight[] => {
  const data = new Uint8Array(Buffer.from(decodeURIComponent(b64), 'base64'))

  const reader = new BufferBitsReader(data)
  const highlights: SerializableHighlight[] = []

  const highlightCount = reader.readBits(12)

  for (let i = 0; i < highlightCount; i++) {
    const rects = []
    const page = reader.readBits(12)
    const length = reader.readBits(12)

    for (let j = 0; j < length; j++) {
      const top = reader.readBits(12) / 4096
      const left = reader.readBits(12) / 4096
      const width = reader.readBits(12) / 4096
      const height = reader.readBits(12) / 4096

      rects.push({ top, left, width, height })
    }

    highlights.push({ page, rects })
  }

  return highlights
}
